<template>
  <BaseLayout :heading="'Verbal Fluency - Supermarket - Transcription'" />

  <div class="instructions">
    <div class="line">Score the verbal fluency test for supermarket items.</div>
  </div>

  <!-- Add AudioPlayer component if there's a saved audio -->
  <div v-if="audioUrl" class="audio-section">
    <AudioPlayer 
      :audioUrl="audioUrl" 
      taskTitle="Supermarket"
      @close="closeAudioPlayer"
    />
  </div>
  <div v-else class="no-audio-message">
    No audio recording available.
  </div>

  <VerbalFluencyInterview
    :jsonData="jsonData"
    :audioFile="audioFile"
    @update:enteredWord="updateEnteredWord"
    @update:selectedAnswer="updateSelectedAnswer"
    @entrySummaryUpdated="updateEntrySummary"
    @update:suggestedWords="updateSuggestedWords"
    @taskDataChanged="handleTaskDataChanged"
  />

  <NotesSection 
    :administeringNotes="jsonData ? jsonData.administeringNotes : ''" 
    :scoringNotes="jsonData ? jsonData.scoringNotes : ''" 
    ref="notesSection" />

  <TextButton buttonText="Submit" :dark="true" type="submit" @click="submitForm" />

  <NotificationPopup 
    :message="notificationMessage" 
    :visible="notificationVisible"
  />

  <ConfirmationPopup
    :visible="showConfirmPopup"
    @confirm="handleConfirmLeave"
    @noSave="handleNoSaveLeave"
    @cancel="showConfirmPopup = false"
  />
</template>

<script>
import BaseLayout from '../BaseLayout.vue';
import VerbalFluencyInterview from '@/components/Pages/VerbalFluencyInterview.vue';
import TaskController from '../../controllers/TaskController';
import ServerCodeModel from '../../models/ServerCodeModel';
import NotificationPopup from '@/components/NotificationPopup.vue';
import ConfirmationPopup from '@/components/ConfirmationPopup.vue';
import NotesSection from '@/components/NotesSection.vue';
import TextButton from '@/components/TextButton.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';

export default {
  components: {
    BaseLayout,
    VerbalFluencyInterview,
    NotificationPopup,
    ConfirmationPopup,
    NotesSection,
    TextButton,
    AudioPlayer
  },
  data() {
    return {
      controller: null,
      jsonData: null,
      notificationMessage: '',
      notificationVisible: false,
      hasUnsavedChanges: false,
      showConfirmPopup: false,
      nextRoute: null,
      forceNavigate: false,
      audioUrl: null,
      audioFile: null
    };
  },
  computed: {
    isAdministering() {
      return false; // This is a scoring view
    }
  },
  async mounted() {
    const serverCodeModel = new ServerCodeModel();
    serverCodeModel.logType = 'InterviewerVerbalFluencySupermarketLog';

    this.controller = new TaskController(serverCodeModel.logType, 'verbalFluencySupermarketData', serverCodeModel);

    await this.loadData();
    
    // If there's an existing audio URL in the data, load it
    if (this.jsonData && this.jsonData.audioUrl) {
      this.audioUrl = this.jsonData.audioUrl;
      console.log('Restored audio URL:', this.audioUrl);
    }
  },
  methods: {
    async loadData() {
      try {
        await this.controller.model.fetchData();
        this.jsonData = this.controller.model.data;
        
        // Ensure entries array exists
        if (!this.jsonData.entries) {
          this.jsonData.entries = [];
        }
        
        // Ensure entrySummary exists
        if (!this.jsonData.entrySummary) {
          this.jsonData.entrySummary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
        }
        
        console.log('Loaded data:', this.jsonData);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    },
    updateEnteredWord(index, value) {
      if (this.jsonData && this.jsonData.entries && this.jsonData.entries[index]) {
        this.jsonData.entries[index].enteredWord = value;
        this.hasUnsavedChanges = true;
      }
    },
    updateSelectedAnswer(index, value) {
      if (this.jsonData && this.jsonData.entries && this.jsonData.entries[index]) {
        this.jsonData.entries[index].selectedAnswer = value;
        this.hasUnsavedChanges = true;
        
        // Update the entry summary
        this.jsonData.entrySummary = this.calculateEntrySummary();
      }
    },
    async submitForm() {
      this.notificationMessage = 'Submitting...';
      this.notificationVisible = true;
      
      try {
        // Validate data if needed
        if (!this.jsonData) {
          throw new Error('No data available to submit');
        }
        
        // Get the latest notes from the notes section
        const notesSection = this.$refs.notesSection;
        this.jsonData.scoringNotes = notesSection.getScoringNotes();
        
        // Submit the data
        await this.controller.submitData(this.jsonData);
        
        this.notificationMessage = 'Submitted Successfully!';
        this.hasUnsavedChanges = false;
      } catch (error) {
        this.notificationMessage = 'Submitting failed. Please try again.';
        console.error('Error submitting form:', error);
      } finally {
        this.resetNotification();
      }
    },
    resetNotification() {
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    async handleConfirmLeave() {
      this.showConfirmPopup = false;
      this.hasUnsavedChanges = false;
      await this.submitForm();
      this.$router.push(this.nextRoute);
    },
    handleNoSaveLeave() {
      this.showConfirmPopup = false;
      this.forceNavigate = true;
      this.hasUnsavedChanges = false;
      this.$router.push(this.nextRoute);
    },
    calculateEntrySummary() {
      const summary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
      
      if (this.jsonData && this.jsonData.entries) {
        this.jsonData.entries.forEach(entry => {
          const selected = (entry.selectedAnswer || '').toLowerCase();
          if (!selected) return;

          const hasI = selected.includes('i');
          const hasR = selected.includes('r');
          const hasQ = selected.includes('q');
          const hasC = selected.includes('c');

          if (hasI && hasR) {
            summary.ir++;
          } else if (hasI) {
            summary.i++;
          } else if (hasR) {
            summary.r++;
          } else if (hasC) {
            summary.c++;
          }

          if (hasQ) {
            summary.q++;
          }
        });
      }
      
      console.log('Calculated summary:', summary);
      return summary;
    },
    updateEntrySummary(summary) {
      if (this.jsonData) {
        this.jsonData.entrySummary = summary;
        this.hasUnsavedChanges = true;
      }
    },
    closeAudioPlayer() {
      this.audioUrl = null;
    },
    updateSuggestedWords(words) {
      if (this.jsonData) {
        // Create a new object to avoid direct mutation
        this.jsonData = {
          ...this.jsonData,
          suggestedWords: words
        };
        
        // Save the data to ensure suggestions are persisted
        this.controller.model.saveData(this.jsonData);
        console.log('Saved suggested words to data:', words);
      }
    },
    handleTaskDataChanged() {
      this.hasUnsavedChanges = true;
      if (this.jsonData) {
        this.controller.model.saveData(this.jsonData);
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    const notesSection = this.$refs.notesSection;
    const currentScoringNotes = notesSection.getScoringNotes();

    if (this.forceNavigate) {
      this.forceNavigate = false;
      this.hasUnsavedChanges = false;
      next();
      return;
    }

    if (currentScoringNotes !== this.jsonData.scoringNotes) {
      this.hasUnsavedChanges = true;
    }

    if (this.hasUnsavedChanges) {
      this.showConfirmPopup = true;
      this.nextRoute = to.fullPath;
      next(false);
    } else {
      // Save data before leaving the page
      this.controller.model.saveData(this.jsonData);
      next();
    }
  }
};
</script>

<style scoped>
.instructions {
  font-size: 18px;
  margin-bottom: 20px;
}
.line {
  margin-bottom: 20px;
}
.audio-section {
  margin-top: 20px;
  margin-bottom: 20px;
}
.no-audio-message {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
  color: #6c757d;
  font-style: italic;
}
</style>